import { Mic, NotebookPen, Bot } from 'lucide-react'
import { Button } from 'antd'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import type { InterviewNotesItem, InterviewQuestion } from '@bandos/shared'
import { SPACING } from '../../../../styles/theme'
import { BackToMindmapCTA } from '../../../../components/common/BackToMindmapCTA'
import {
  Callout,
  CalloutHeader,
  CalloutIcon,
  CalloutTitle,
  CalloutBody,
  CalloutActions,
  CalloutFootnote,
} from '../../../../components/common/synthesisCallout'
import { InterviewSummaryCard } from './InterviewSummaryCard'

export interface InterviewSynthesisTabProps {
  notes: InterviewNotesItem[]
  questions: InterviewQuestion[]
  nodeId: string
  mindmapId: string
  onSimulate: () => void
  simulating: boolean
}

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xl};
`

export function InterviewSynthesisTab({
  notes,
  questions,
  nodeId,
  mindmapId,
  onSimulate,
  simulating,
}: InterviewSynthesisTabProps) {
  const navigate = useNavigate()

  if (notes.length === 0) {
    return (
      <Callout>
        <CalloutHeader>
          <CalloutIcon>
            <Mic size={20} />
          </CalloutIcon>
          <CalloutTitle>No interviews recorded yet</CalloutTitle>
        </CalloutHeader>
        <CalloutBody>
          Record your first conversation to start gathering qualitative signal. After each
          interview, you'll see an AI-generated summary here with a key quote, observations, and
          unmet needs.
        </CalloutBody>
        <CalloutActions>
          <Button
            type="primary"
            icon={<NotebookPen size={15} />}
            onClick={() => void navigate(`/interview/${nodeId}/conduct`)}
          >
            Record interview
          </Button>
          <Button
            icon={<Bot size={15} />}
            loading={simulating}
            disabled={simulating}
            onClick={onSimulate}
          >
            Simulate responses
          </Button>
        </CalloutActions>
        <CalloutFootnote>
          Each interview also updates the confidence scores on your mindmap nodes.
        </CalloutFootnote>
      </Callout>
    )
  }

  const hasAnySynthesis = notes.some((n) => n.aiSummaryStructured != null || n.aiSummary != null)

  return (
    <>
      <Grid>
        {notes.map((note, i) => (
          <InterviewSummaryCard
            key={note.id}
            note={note}
            index={i}
            questions={questions}
            nodeId={nodeId}
          />
        ))}
      </Grid>
      {hasAnySynthesis && <BackToMindmapCTA mindmapId={mindmapId} />}
    </>
  )
}
